/**
 * One ticket, from the staff chair (CLAUDE.md 12.2, 12.3).
 *
 * Thread on the left, everything about the ticket on the right: status, priority
 * and assignee next to the reporter, the workspace and the frozen position
 * snapshot. The snapshot is the reason this page earns its keep -- it is the exact
 * input we would replay, so it can be copied or saved as a file, never retyped.
 *
 * "Reply and resolve" is one action because that is how most threads end.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Check, ChevronLeft, Copy, Download, Send, UserCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useAdminTicket, useAdminUsers, useReplyToTicket, useUpdateAdminTicket } from '@/api/admin'
import { ApiError } from '@/api/client'
import { attachmentFilePath, uploadAttachments, useTicketLimits } from '@/api/support'
import { useAuth } from '@/auth/AuthProvider'
import { AdminShell, formatDateTime } from '@/components/admin/AdminShell'
import { AttachmentPicker, useAttachmentDraft } from '@/components/support/AttachmentPicker'
import { MarkdownEditor } from '@/components/support/MarkdownEditor'
import {
  TicketPriorityPill,
  TicketStatusPill,
  TicketTypeIcon,
} from '@/components/support/TicketBadges'
import { TicketThread } from '@/components/support/TicketThread'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { NotFoundPage } from '@/pages/NotFoundPage'

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const

const selectClass = 'h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-text'

export function AdminTicketPage() {
  const { t } = useTranslation(['admin', 'common'])
  const { toast } = useToast()
  const { user } = useAuth()
  const params = useParams<{ ticketId: string }>()
  const ticketId = Number(params.ticketId)

  const ticket = useAdminTicket(Number.isFinite(ticketId) ? ticketId : null)
  const staff = useAdminUsers({ system_role: 'sysadmin' })
  const update = useUpdateAdminTicket()
  const reply = useReplyToTicket(ticketId)
  const limits = useTicketLimits()
  const draft = useAttachmentDraft(limits.data)

  const [body, setBody] = useState('')
  const [uploading, setUploading] = useState(false)
  const [copied, setCopied] = useState(false)
  const busy = reply.isPending || uploading

  if (ticket.isError) return <NotFoundPage />

  const fail = (error: unknown) =>
    toast({
      title: t('common.error', { ns: 'common' }),
      description: error instanceof ApiError ? error.localised : undefined,
      variant: 'error',
    })

  const patch = (input: { status?: string; priority?: string; assignee_id?: number }) =>
    update.mutate(
      { ticketId, ...input },
      {
        onSuccess: () => toast({ title: t('common.saved', { ns: 'common' }), variant: 'success' }),
        onError: fail,
      },
    )

  const send = async (resolve: boolean) => {
    if (busy || !body.trim()) return
    let messageId: number
    try {
      const message = await reply.mutateAsync({ body, resolve })
      messageId = message.id
    } catch (error) {
      fail(error)
      return
    }
    if (draft.files.length > 0) {
      setUploading(true)
      try {
        await uploadAttachments(ticketId, draft.files, { messageId, admin: true })
      } catch (error) {
        toast({
          title: t('support.attachments.upload_failed', { ns: 'common' }),
          description: error instanceof ApiError ? error.localised : undefined,
          variant: 'error',
        })
      } finally {
        setUploading(false)
      }
    }
    setBody('')
    draft.clear()
    void ticket.refetch()
  }

  const data = ticket.data
  const snapshotJson = data?.position_snapshot
    ? JSON.stringify(data.position_snapshot, null, 2)
    : null

  const copySnapshot = async () => {
    if (!snapshotJson) return
    try {
      await navigator.clipboard.writeText(snapshotJson)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast({ title: t('common.error', { ns: 'common' }), variant: 'error' })
    }
  }

  const downloadSnapshot = () => {
    if (!snapshotJson || !data) return
    const url = URL.createObjectURL(new Blob([snapshotJson], { type: 'application/json' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `ticket-${data.id}-position.json`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <AdminShell>
      <div className="space-y-4">
        <Link
          to="/admin/tickets"
          className="inline-flex h-9 items-center gap-1 text-sm text-text-muted hover:text-text"
        >
          <ChevronLeft size={15} aria-hidden />
          {t('admin.tickets.title')}
        </Link>

        {!data && <Skeleton className="h-64" />}

        {data && (
          <>
            <header className="space-y-2">
              <div className="flex flex-wrap items-start gap-2">
                <h2 className="min-w-0 flex-1 text-lg font-semibold leading-snug">{data.title}</h2>
                <TicketStatusPill status={data.status} className="mt-1" />
                <TicketPriorityPill priority={data.priority} />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
                <span className="inline-flex items-center gap-1">
                  <TicketTypeIcon type={data.type} size={13} />
                  {t(`support.type.${data.type}`, { ns: 'common' })}
                </span>
                <span>#{data.id}</span>
                <span>{formatDateTime(data.created_at)}</span>
                {data.module_id && <span className="font-mono">{data.module_id}</span>}
                {data.awaiting_staff && data.status !== 'closed' && (
                  <span className="font-medium text-accent">{t('admin.tickets.needs_reply')}</span>
                )}
              </div>
            </header>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="space-y-4">
                <TicketThread
                  ticket={data}
                  reporterName={data.reporter_display_name ?? data.reporter_username ?? '—'}
                  filePath={(attachment, inline) =>
                    attachmentFilePath(ticketId, attachment.id, { admin: true, inline })
                  }
                />

                <Card>
                  <CardBody className="space-y-3">
                    <h3 className="text-sm font-semibold">{t('admin.tickets.reply')}</h3>
                    <MarkdownEditor
                      value={body}
                      onChange={setBody}
                      onPasteFiles={draft.add}
                      onSubmit={() => void send(false)}
                      rows={5}
                      disabled={busy}
                      placeholder={t('admin.tickets.reply_placeholder')}
                    />
                    <AttachmentPicker draft={draft} disabled={busy} compact />
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <Button
                        variant="secondary"
                        loading={busy}
                        disabled={!body.trim()}
                        onClick={() => void send(true)}
                      >
                        <Check size={15} aria-hidden />
                        {t('admin.tickets.reply_resolve')}
                      </Button>
                      <Button
                        variant="primary"
                        loading={busy}
                        disabled={!body.trim()}
                        onClick={() => void send(false)}
                      >
                        <Send size={15} aria-hidden />
                        {t('admin.tickets.reply')}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </div>

              <aside className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('admin.tickets.triage')}</CardTitle>
                  </CardHeader>
                  <CardBody className="space-y-3 text-sm">
                    <label className="block space-y-1">
                      <span className="text-xs font-medium text-text-muted">
                        {t('common.status', { ns: 'common' })}
                      </span>
                      <select
                        className={selectClass}
                        value={data.status}
                        onChange={(event) => patch({ status: event.target.value })}
                      >
                        {STATUSES.map((value) => (
                          <option key={value} value={value}>
                            {t(`support.status.${value}`, { ns: 'common' })}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block space-y-1">
                      <span className="text-xs font-medium text-text-muted">
                        {t('admin.tickets.priority')}
                      </span>
                      <select
                        className={selectClass}
                        value={data.priority}
                        onChange={(event) => patch({ priority: event.target.value })}
                      >
                        {PRIORITIES.map((value) => (
                          <option key={value} value={value}>
                            {t(`support.priority.${value}`, { ns: 'common' })}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block space-y-1">
                      <span className="text-xs font-medium text-text-muted">
                        {t('admin.tickets.assignee')}
                      </span>
                      {/* `0` unassigns -- the API reads it as "no assignee". */}
                      <select
                        className={selectClass}
                        value={String(data.assignee_id ?? 0)}
                        onChange={(event) => patch({ assignee_id: Number(event.target.value) })}
                      >
                        <option value="0">{t('admin.tickets.unassigned')}</option>
                        {staff.data?.items.map((person) => (
                          <option key={person.id} value={person.id}>
                            {person.display_name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {user && data.assignee_id !== user.id && (
                      <Button size="sm" onClick={() => patch({ assignee_id: user.id })}>
                        <UserCheck size={14} aria-hidden />
                        {t('admin.tickets.assign_me')}
                      </Button>
                    )}
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('admin.tickets.reporter')}</CardTitle>
                  </CardHeader>
                  <CardBody className="space-y-1 text-sm">
                    <div className="font-medium">{data.reporter_display_name ?? '—'}</div>
                    <div className="text-text-muted">@{data.reporter_username ?? '—'}</div>
                    {data.reporter_email && (
                      <a
                        href={`mailto:${data.reporter_email}`}
                        className="block truncate text-accent underline-offset-2 hover:underline"
                      >
                        {data.reporter_email}
                      </a>
                    )}
                    <div className="pt-2 text-xs text-text-muted">
                      {t('admin.tickets.workspace')}: {data.workspace_name ?? '—'}
                    </div>
                    <div className="text-xs text-text-muted">
                      {t('common.updated', { ns: 'common' })}:{' '}
                      {formatDateTime(data.last_activity_at)}
                    </div>
                  </CardBody>
                </Card>

                {snapshotJson && (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('admin.tickets.snapshot')}</CardTitle>
                    </CardHeader>
                    <CardBody className="space-y-2">
                      <p className="text-xs text-text-muted">{t('admin.tickets.snapshot_hint')}</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => void copySnapshot()}>
                          {copied ? (
                            <Check size={14} aria-hidden />
                          ) : (
                            <Copy size={14} aria-hidden />
                          )}
                          {copied
                            ? t('common.saved', { ns: 'common' })
                            : t('action.copy', { ns: 'common' })}
                        </Button>
                        <Button size="sm" onClick={downloadSnapshot}>
                          <Download size={14} aria-hidden />
                          {t('action.download', { ns: 'common' })}
                        </Button>
                      </div>
                      <details>
                        <summary className="cursor-pointer text-xs font-medium">
                          {t('admin.tickets.snapshot_show')}
                        </summary>
                        {/* Frozen at the moment the ticket was raised (12.3). Shown raw: it
                            is the exact input we would replay. */}
                        <pre className="mt-2 max-h-96 overflow-auto rounded bg-surface-raised p-2 font-mono text-xs">
                          {snapshotJson}
                        </pre>
                      </details>
                    </CardBody>
                  </Card>
                )}
              </aside>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  )
}
